/**
 * DuplicadosDialog — Modal genérico para revisar y resolver duplicados detectados
 * por el backend (endpoint `/api/<entidad>/duplicados-candidatos`).
 *
 * Recibe pares {a, b, razon, similitud}. Para cada par permite:
 *   • Abrir A / Abrir B (edita o navega)
 *   • Eliminar A o B (DELETE /<entidad>/{id})
 *   • Descartar par (oculto local hasta cerrar)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Pencil, Trash2, X as XIcon, Loader2 } from "lucide-react";

export default function DuplicadosDialog({
  open, onOpenChange,
  entidad,                   // "espacios" | "clientes"
  fetchPath,                 // ej. "/espacios/duplicados-candidatos"
  deletePath,                // ej. "/espacios"
  renderItem,                // (item) => ReactNode  pinta cada lado del par
  onEdit,                    // (item) => void  abre el form de edición del padre
  onAfterChange,             // () => void  recargar listado padre
}) {
  const [loading, setLoading] = useState(false);
  const [pares, setPares] = useState([]);
  const [total, setTotal] = useState(0);
  const [umbral, setUmbral] = useState(0.85);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [working, setWorking] = useState(null); // id en curso de borrado

  const load = async (u = umbral) => {
    setLoading(true);
    try {
      const { data } = await api.get(fetchPath, { params: { threshold: u } });
      setPares(data.pares || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando duplicados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setDismissed(new Set()); load(umbral); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibles = pares.filter((p) => !dismissed.has(`${p.a.id}|${p.b.id}`));

  const handleDelete = async (lado, par) => {
    const it = par[lado];
    if (!window.confirm(`¿Eliminar "${it.nombre}"?\n\n⚠ Asegúrate de que has migrado primero las referencias (campañas, espacios, etc.) al otro registro.`)) return;
    setWorking(it.id);
    try {
      await api.delete(`${deletePath}/${it.id}`);
      toast.success("Eliminado");
      // Quitar del listado local
      setPares((prev) => prev.filter((p) => p.a.id !== it.id && p.b.id !== it.id));
      onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setWorking(null);
    }
  };

  const dismiss = (par) => {
    setDismissed((prev) => new Set(prev).add(`${par.a.id}|${par.b.id}`));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
        data-testid={`duplicados-dialog-${entidad}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Posibles duplicados — {entidad === "espacios" ? "Espacios" : "Clientes"}
          </DialogTitle>
        </DialogHeader>

        {/* Controls */}
        <div className="flex items-center gap-3 pb-3 border-b border-zinc-800 flex-wrap">
          <div className="text-xs text-zinc-400">
            Umbral similitud:
          </div>
          <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/70 p-0.5">
            {[0.75, 0.85, 0.92].map((u) => (
              <button
                type="button" key={u}
                onClick={() => { setUmbral(u); load(u); }}
                data-testid={`duplicados-umbral-${Math.round(u * 100)}`}
                className={`px-2.5 py-1 text-xs rounded transition ${
                  Math.abs(umbral - u) < 0.001
                    ? "bg-amber-500 text-zinc-900 font-semibold"
                    : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                ≥ {Math.round(u * 100)}%
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs text-zinc-500" data-testid="duplicados-count">
            {loading ? "Buscando…" : `${visibles.length} de ${total} pares`}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto py-3 space-y-3">
          {loading && (
            <div className="text-center py-12 text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando candidatos…
            </div>
          )}
          {!loading && visibles.length === 0 && (
            <div className="text-center py-12 text-emerald-400 text-sm">
              🎉 No se han detectado duplicados con este umbral.
            </div>
          )}
          {!loading && visibles.map((par, i) => (
            <div
              key={`${par.a.id}-${par.b.id}-${i}`}
              data-testid={`duplicado-par-${par.a.id}-${par.b.id}`}
              className="border border-zinc-800 rounded-lg bg-zinc-900/40"
            >
              <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between gap-2">
                <div className="text-[11px] text-amber-300/90 truncate">
                  {par.razon}
                </div>
                <button
                  onClick={() => dismiss(par)}
                  data-testid={`duplicado-descartar-${par.a.id}-${par.b.id}`}
                  className="text-zinc-500 hover:text-zinc-200 text-[11px] flex items-center gap-1"
                  title="Descartar este par (solo en esta sesión)"
                >
                  <XIcon className="w-3 h-3" /> Descartar
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-zinc-800">
                {["a", "b"].map((lado) => (
                  <div key={lado} className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {lado === "a" ? "Registro A" : "Registro B"}
                    </div>
                    {renderItem ? renderItem(par[lado]) : (
                      <div className="text-sm">
                        <div className="font-semibold">{par[lado].nombre}</div>
                        <div className="text-xs text-zinc-500">{par[lado].id}</div>
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => onEdit?.(par[lado])}
                        data-testid={`duplicado-editar-${lado}-${par[lado].id}`}
                        className="h-7 px-2 text-xs text-zinc-300 hover:text-amber-300"
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => handleDelete(lado, par)}
                        disabled={working === par[lado].id}
                        data-testid={`duplicado-eliminar-${lado}-${par[lado].id}`}
                        className="h-7 px-2 text-xs text-zinc-500 hover:text-red-400"
                      >
                        {working === par[lado].id ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3 mr-1" />
                        )}
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="border-t border-zinc-800 pt-3">
          <Button
            type="button" variant="outline"
            onClick={() => load(umbral)}
            data-testid="duplicados-refrescar"
            className="bg-transparent border-zinc-700 text-zinc-200"
          >
            Refrescar
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="duplicados-cerrar"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
